/**
 * Form Schema to FormDefinition Converter (Issue #658)
 *
 * Converts backend GraphQL FormSchema to frontend FormDefinition
 * for use with the existing FormRenderer infrastructure.
 *
 * ## Architecture
 *
 * This converter bridges the gap between:
 * - Backend: `GqlFormSchema` from form_schema_api (subgraph-defined)
 * - Frontend: `FormDefinition` for DSL-driven FormRenderer
 *
 * ## Life-Safety Mapping
 *
 * Safety levels are preserved:
 * - STANDARD -> 'standard'
 * - ELEVATED -> 'elevated'
 * - CRITICAL -> 'critical'
 * - LIFE_CRITICAL -> 'life-critical'
 */

import type {
  FormDefinition,
  FieldDefinition,
  FieldType,
  StepDefinition,
  OptionItem,
  DataSource,
  ConditionOperator,
  Condition,
  FieldValidation,
} from '@/renderer/types';
import type {
  FormSchema,
  FormFieldDefinition,
  FormFieldType,
  FormSafetyLevel,
  FormLayout,
  SelectOption,
  FieldCondition,
  ConditionOperator as GqlConditionOperator,
  ReferenceConfig,
  FormValidationRule,
} from '@/generated/graphql';

// =============================================================================
// TYPE MAPPERS
// =============================================================================

/**
 * Map GraphQL FormFieldType to frontend FieldType
 */
function mapFieldType(gqlType: FormFieldType | '%future added value'): FieldType {
  // @ts-expect-error TS2739
  const typeMap: Record<FormFieldType, FieldType> = {
    TEXT: 'text',
    TEXTAREA: 'textarea',
    DATE: 'date',
    DATETIME: 'datetime',
    TIME: 'time',
    NUMBER: 'number',
    CURRENCY: 'currency',
    SELECT: 'select',
    MULTISELECT: 'multiselect',
    REFERENCE: 'select', // Reference fields use select with GraphQL data source
    BOOLEAN: 'checkbox',
    TOGGLE: 'switch',
    FILE: 'file',
    ADDRESS: 'address',
    PHONE: 'phone',
    EMAIL: 'email',
    SSN: 'text', // SSN uses masked text input
    MRN: 'text', // MRN uses text input
    NPI: 'text', // NPI uses text input
    COMPOSITE: 'custom', // Composite fields need custom rendering
    READONLY: 'text', // Readonly renders as disabled text
    HIDDEN: 'text', // Hidden fields still have text type
    JSON: 'json',
    CODE: 'code',
    RICH_TEXT: 'rich-text',
    SIGNATURE: 'signature',
    RATING: 'rating',
  };

  return typeMap[gqlType as FormFieldType] || 'text';
}

/**
 * Map GraphQL SafetyLevel to frontend string
 */
function mapSafetyLevel(
  level: FormSafetyLevel | '%future added value'
): 'standard' | 'elevated' | 'critical' | 'life-critical' {
  const levelMap: Record<
    FormSafetyLevel,
    'standard' | 'elevated' | 'critical' | 'life-critical'
  > = {
    STANDARD: 'standard',
    ELEVATED: 'elevated',
    CRITICAL: 'critical',
    LIFE_CRITICAL: 'life-critical',
  };

  return levelMap[level as FormSafetyLevel] || 'standard';
}

/**
 * Map GraphQL ConditionOperator to frontend operator
 */
function mapConditionOperator(op: GqlConditionOperator | '%future added value'): ConditionOperator {
  const opMap: Record<GqlConditionOperator, ConditionOperator> = {
    EQ: 'eq',
    NE: 'ne',
    GT: 'gt',
    GTE: 'gte',
    LT: 'lt',
    LTE: 'lte',
    IN: 'in',
    NOT_IN: 'notIn',
    CONTAINS: 'contains',
    IS_EMPTY: 'isEmpty',
    IS_NOT_EMPTY: 'isNotEmpty',
  };

  return opMap[op as GqlConditionOperator] || 'eq';
}

// =============================================================================
// OPTION MAPPERS
// =============================================================================

/**
 * Convert GraphQL SelectOption to frontend OptionItem
 */
function mapSelectOption(opt: SelectOption): OptionItem {
  return {
    value: opt.value,
    label: opt.label,
    disabled: opt.disabled ?? false,
    description: opt.description ?? undefined,
  };
}

/**
 * Create data source for reference fields
 */
function mapReferenceConfig(config: ReferenceConfig): DataSource {
  return {
    type: 'graphql',
    query: config.query ?? undefined,
    valuePath: config.valuePath ?? undefined,
    labelPath: config.labelPath ?? undefined,
    variables: config.searchFields && config.searchFields.length > 0
      ? { searchFields: config.searchFields }
      : undefined,
    cacheTTL: 60, // 1 minute cache for reference lookups
  };
}

// =============================================================================
// CONDITION MAPPERS
// =============================================================================

/**
 * Convert GraphQL FieldCondition to frontend Condition
 */
function mapCondition(gqlCondition: FieldCondition): Condition {
  return {
    field: gqlCondition.field,
    operator: mapConditionOperator(gqlCondition.operator),
    value: gqlCondition.value ?? undefined,
  };
}

// =============================================================================
// VALIDATION MAPPERS
// =============================================================================

/**
 * Extract field-level validation from GraphQL field definition
 */
function mapFieldValidation(field: FormFieldDefinition): FieldValidation {
  return {
    required: field.required,
    minLength: field.minLength ?? undefined,
    maxLength: field.maxLength ?? undefined,
    pattern: field.pattern ?? undefined,
    patternMessage: field.patternMessage ?? undefined,
    min: field.min ?? undefined,
    max: field.max ?? undefined,
  };
}

/**
 * Map form-level validation rules
 */
function mapValidationRules(
  rules?: FormValidationRule[]
): FormDefinition['crossFieldValidation'] {
  if (!rules || rules.length === 0) return undefined;

  return rules.map((rule) => ({
    fields: rule.fields,
    rule: rule.expression,
    message: rule.message,
  }));
}

// =============================================================================
// FIELD MAPPER
// =============================================================================

/**
 * Convert GraphQL FormFieldDefinition to frontend FieldDefinition
 */
function mapField(gqlField: FormFieldDefinition): FieldDefinition {
  const fieldDef: FieldDefinition = {
    name: gqlField.name,
    type: mapFieldType(gqlField.fieldType),
    label: gqlField.label,
    placeholder: gqlField.placeholder ?? undefined,
    description: gqlField.helpText ?? undefined,
    helpText: gqlField.helpText ?? undefined,
    defaultValue: gqlField.defaultValue ?? undefined,
    validation: mapFieldValidation(gqlField),
  };

  // Handle static options
  if (gqlField.options && gqlField.options.length > 0) {
    fieldDef.options = gqlField.options.map(mapSelectOption);
  }

  // Handle reference fields (entity lookups)
  if (gqlField.referenceConfig) {
    fieldDef.dataSource = mapReferenceConfig(gqlField.referenceConfig);
  }

  // Handle visibility conditions
  if (gqlField.visibleWhen || gqlField.disabledWhen) {
    fieldDef.visibility = {};
    if (gqlField.visibleWhen) {
      fieldDef.visibility.showWhen = mapCondition(gqlField.visibleWhen);
    }
    if (gqlField.disabledWhen) {
      fieldDef.visibility.disableWhen = mapCondition(gqlField.disabledWhen);
    }
  }

  // Handle readonly type
  if (gqlField.fieldType === 'READONLY') {
    fieldDef.props = { ...fieldDef.props, readOnly: true };
  }

  // Handle hidden type
  if (gqlField.fieldType === 'HIDDEN') {
    fieldDef.visibility = {
      ...fieldDef.visibility,
      hideWhen: { field: '_always', operator: 'eq', value: true },
    };
  }

  // Store PII classification in metadata for audit purposes
  if (gqlField.piiClassification) {
    fieldDef.metadata = {
      ...fieldDef.metadata,
      piiClassification: gqlField.piiClassification,
      auditRequired: gqlField.auditRequired ?? false,
      maskOnDisplay: gqlField.maskOnDisplay ?? false,
    };
  }

  return fieldDef;
}

// =============================================================================
// LAYOUT MAPPER
// =============================================================================

/**
 * Convert GraphQL layout sections to frontend steps/sections
 */
function mapLayoutToSteps(
  layout: FormLayout,
  fields: FieldDefinition[]
): { steps?: StepDefinition[]; sections?: FormDefinition['sections'] } {
  if (!layout.sections || layout.sections.length === 0) {
    return {};
  }

  // Create field lookup by name
  const fieldMap = new Map(fields.map((f) => [f.name, f]));

  if (layout.layoutType === 'wizard' || layout.layoutType === 'WIZARD') {
    // Wizard layout -> steps
    const steps: StepDefinition[] = layout.sections.map((section, index) => ({
      id: `step_${index}`,
      title: section.title,
      description: section.description ?? undefined,
      fields: section.fields
        .map((fieldName) => fieldMap.get(fieldName))
        .filter(Boolean) as FieldDefinition[],
      collapsible: section.collapsible ?? false,
    }));
    return { steps };
  }

  // Single-page or tabbed layout -> sections
  const sections: FormDefinition['sections'] = layout.sections.map(
    (section, index) => ({
      id: `section_${index}`,
      title: section.title,
      description: section.description ?? undefined,
      fields: section.fields,
      collapsible: section.collapsible ?? false,
      collapsed: section.collapsed ?? false,
    })
  );
  return { sections };
}

/**
 * Map layout type string
 */
function mapLayoutType(
  layoutType: string
): 'vertical' | 'horizontal' | 'inline' | 'grid' | 'wizard' {
  const layoutMap: Record<string, FormDefinition['layout']> = {
    'single-page': 'vertical',
    SINGLE_PAGE: 'vertical',
    wizard: 'wizard',
    WIZARD: 'wizard',
    tabbed: 'vertical', // Tabs handled via sections
    TABBED: 'vertical',
    accordion: 'vertical', // Accordion handled via collapsible sections
    ACCORDION: 'vertical',
    grid: 'grid',
    GRID: 'grid',
  };

  return layoutMap[layoutType] || 'vertical';
}

// =============================================================================
// MAIN CONVERTER
// =============================================================================

/**
 * Convert GraphQL FormSchema to frontend FormDefinition
 *
 * @param schema - The GraphQL FormSchema from the backend
 * @returns FormDefinition compatible with FormRenderer
 */
export function schemaToFormDefinition(schema: FormSchema): FormDefinition {
  // Map all fields first
  const fields = schema.fields.map(mapField);

  // Build layout structure
  const { steps, sections } = mapLayoutToSteps(schema.layout, fields);

  // Determine if form requires confirmation (safety-based)
  const safetyLevel = mapSafetyLevel(schema.safetyLevel);
  const requiresConfirmation =
    safetyLevel === 'critical' || safetyLevel === 'life-critical';

  const formDef: FormDefinition = {
    // Identity
    id: `${schema.subgraph}_${schema.entityType}`,
    version: schema.version,
    name: schema.displayName,
    description: schema.description ?? undefined,

    // Structure
    layout: mapLayoutType(schema.layout.layoutType),
    fields,
    steps,
    sections,

    // Validation
    crossFieldValidation: mapValidationRules(schema.validations ?? undefined),

    // Submission - use schema-defined mutations
    submission: {
      mutation: schema.createMutation ?? undefined,
      confirmation: requiresConfirmation
        ? {
            enabled: true,
            title:
              safetyLevel === 'life-critical'
                ? 'Life-Critical Operation'
                : 'Confirm Submission',
            message:
              safetyLevel === 'life-critical'
                ? 'This action may affect patient safety. Please verify all information is correct before proceeding.'
                : 'Are you sure you want to submit this form?',
            confirmText: 'Confirm',
            cancelText: 'Cancel',
            type: safetyLevel === 'life-critical' ? 'danger' : 'warning',
          }
        : undefined,
    },

    // Safety
    safetyLevel,
    auditRequired:
      safetyLevel === 'critical' || safetyLevel === 'life-critical',
    dualApproval: safetyLevel === 'life-critical',

    // Metadata
    metadata: {
      subgraph: schema.subgraph,
      entityType: schema.entityType,
      fetchQuery: schema.fetchQuery,
      updateMutation: schema.updateMutation,
      createdAt: schema.createdAt,
      updatedAt: schema.updatedAt,
    },
  };

  return formDef;
}

/**
 * Merge form definition with initial entity data (for edit mode)
 */
export function mergeWithEntityData(
  formDef: FormDefinition,
  entityData: Record<string, unknown>
): FormDefinition {
  // Apply entity values as default values to fields
  const fieldsWithValues = formDef.fields?.map((field) => ({
    ...field,
    defaultValue: entityData[field.name] ?? field.defaultValue,
  }));

  // Also update steps if they contain fields
  const stepsWithValues = formDef.steps?.map((step) => ({
    ...step,
    fields: step.fields.map((field) => ({
      ...field,
      defaultValue: entityData[field.name] ?? field.defaultValue,
    })),
  }));

  return {
    ...formDef,
    fields: fieldsWithValues,
    steps: stepsWithValues,
    // Update submission to use update mutation for edit mode
    submission: {
      ...formDef.submission,
      mutation:
        (formDef.metadata?.updateMutation as string) ??
        formDef.submission?.mutation,
    },
  };
}

export default schemaToFormDefinition;
